<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { nextTick, onMounted, onBeforeUnmount, watch } from "vue";
  import WrapWithHint from "./wrap-with-hint.vue";
  import LotCodeCombobox from "./lot-code-combobox.vue";
  import { vFieldErrors } from "~/jose_modern/field_with_errors";
  import { quantityWithUnit } from "~/jose_modern/quantity_with_unit";

  const props = defineProps({
    lotCodes: { type: Array, default: () => [] },
    openLotCodes: { type: Array, default: () => [] },
    openLotCodesCount: { type: Number, default: 0 },
    openLotCodesUrl: { type: String, default: "" },
    unit: { type: String, default: "" },
    requiredQuantity: { type: Number, default: null },
    quantityInputId: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    lotCodeRequired: { type: Boolean, default: false },
  });

  const emit = defineEmits(["change"]);

  const NEW_LOT_HINT =
    "This lot code isn't in your inventory. Saving will create it for this run -- " +
    "with no stock to draw from, it records as a negative lot and closes immediately.";

  const DUPLICATE_LOT_HINT =
    "This inventory lot is used by more than one row. Pick a different lot or remove a row " +
    "so each lot appears once before saving. Alternatively, autofill all lots again.";

  const rootEl = _ref(null);

  let quiet = _ref(true);

  let actualQuantityInput = null;
  let liveActualQuantity = _ref(null);

  function updateActualQuantity() {
    const raw = actualQuantityInput?.value;
    const parsed = raw == null || raw === "" ? null : Number(raw);
    liveActualQuantity.value = Number.isFinite(parsed) ? parsed : null;
  }

  if (props.quantityInputId) {
    actualQuantityInput = document.getElementById(props.quantityInputId);
    updateActualQuantity();
  }

  onMounted(() => {
    // Announce initial state so the page can disable saving for a run that opens already
    // invalid (e.g. duplicate lot codes from legacy data), without waiting for an edit.
    emitChange(false);

    if (!actualQuantityInput) return;

    actualQuantityInput.addEventListener("input", updateActualQuantity);
    actualQuantityInput.addEventListener("change", updateActualQuantity);
  });

  onBeforeUnmount(() => {
    actualQuantityInput?.removeEventListener("input", updateActualQuantity);
    actualQuantityInput?.removeEventListener("change", updateActualQuantity);
  });

  const targetQuantity = _computed(() =>
    liveActualQuantity.value != null ? liveActualQuantity.value : props.requiredQuantity
  );

  const requiresLotCode = _computed(() => props.lotCodeRequired && liveActualQuantity.value > 0);

  function lotCodeIdentity(lot) {
    if (lot.persisted_lot_code_id != null) return `plc:${lot.persisted_lot_code_id}`;
    return `code:${(lot.lot_code ?? "").trim()}`;
  }

  const EMPTY_LOT_CODE = lotCodeIdentity({ lot_code: "", persisted_lot_code_id: null });

  function lotCodeChanged(lot) {
    return lotCodeIdentity(lot) !== lot._originalLotCode;
  }

  function normalizeQuantity(value) {
    return value == null || value === "" ? null : Number(value);
  }

  function lotQuantityChanged(lot) {
    return normalizeQuantity(lot.quantity) !== lot._originalQuantity;
  }

  function matchLoneLotToAmount(lot) {
    lot.quantity = targetQuantity.value;
    lot._originalQuantity = targetQuantity.value;
  }

  let lots = _ref(
    props.lotCodes.map((lc) => ({
      ...lc,
      persisted_lot_code_id: lc.persisted_lot_code_id ?? null,
      quantity: normalizeQuantity(lc.quantity),
      ...remainingFields(lc),
      _originalLotCode: lotCodeIdentity({
        persisted_lot_code_id: lc.persisted_lot_code_id ?? null,
        lot_code: lc.lot_code,
      }),
      _originalQuantity: normalizeQuantity(lc.quantity),
    }))
  );

  if (lots.value.length === 0) {
    lots.value.push({
      lot_code: "",
      quantity: null,
      persisted_lot_code_id: null,
      _originalLotCode: EMPTY_LOT_CODE,
      _originalQuantity: null,
    });
  }

  // A single lot always takes the full amount. This is display-only: the run isn't blocked
  // as over/short, and close normalizes the lot server-side regardless
  if (lots.value.length === 1 && targetQuantity.value != null && lots.value[0].quantity !== targetQuantity.value) {
    matchLoneLotToAmount(lots.value[0]);
  }

  function remainingFields(source) {
    const displayQuantity =
      source.remaining_quantity == null ? null : Number(source.remaining_quantity);
    return {
      remaining_in_unit:
        source.remaining_quantity_units == null ? null : Number(source.remaining_quantity_units),
      remaining:
        displayQuantity == null
          ? null
          : quantityWithUnit(round(displayQuantity), source.remaining_unit ?? props.unit),
    };
  }

  const hasUnseededOpenLots = _computed(() => props.openLotCodesCount > props.openLotCodes.length);

  const openLotOptions = _computed(() =>
    props.openLotCodes.map((plc) => ({
      value: plc.id,
      persisted_lot_code_id: plc.id,
      lot_code: plc.lot_code,
      date: plc.date ?? null,
      ...remainingFields(plc),
      label: plc.label ?? plc.lot_code,
    }))
  );

  function lotValue(lot) {
    if (lot.persisted_lot_code_id != null) return lot.persisted_lot_code_id;
    if (lotHasCode(lot)) return `custom:${lot.lot_code}`;
    return "";
  }

  // "New" means the lot code has no persisted lot code backing it.
  function lotIsNew(lot) {
    return lotHasCode(lot) && lot.persisted_lot_code_id == null;
  }

  function lotOption(lot) {
    if (lot.persisted_lot_code_id == null) return null;
    // A selected lot carries its own remaining data, so validation holds even when
    // it falls outside the seeded page; fall back to the seed for legacy callers.
    if (lot.remaining_in_unit != null || lot.remaining != null) return lot;
    return (
      openLotOptions.value.find((o) => o.persisted_lot_code_id === lot.persisted_lot_code_id) ?? null
    );
  }

  function lotRemaining(lot) {
    return lotOption(lot)?.remaining_in_unit ?? null;
  }

  function lotDrawnQuantity(lot) {
    if (isMulti.value) return Number(lot.quantity) || 0;
    return targetQuantity.value ?? 0;
  }

  // Drawing more than a PLC has left records it as a negative lot. New lots have no
  // stock at all and already carry the "New" cue, so we don't double-flag them.
  function lotIsNegative(lot) {
    if (lotIsNew(lot)) return false;

    const remaining = lotRemaining(lot);
    if (remaining == null) return false;

    return round(lotDrawnQuantity(lot) - remaining) > 0;
  }

  function negativeLotHint(lot) {
    const remainingText = lotOption(lot)?.remaining ?? "what's left";
    return (
      `This draws more than the ${remainingText} remaining in this lot. ` +
      "Saving will record it as a negative lot and close it."
    );
  }

  function lotOptions(lot) {
    const options = openLotOptions.value;
    const value = lotValue(lot);
    if (value === "" || options.some((option) => option.value === value)) return options;

    // The current selection isn't in the seed: either a brand-new code with no
    // PLC (show it as a "Create lot" row) or a real lot outside the seed, which
    // carries its own remaining data so it renders like any other option.
    return [
      {
        value,
        persisted_lot_code_id: lot.persisted_lot_code_id ?? null,
        lot_code: lot.lot_code,
        date: lot.date ?? null,
        remaining_in_unit: lot.remaining_in_unit ?? null,
        remaining: lot.remaining ?? null,
        label: lot.label ?? lot.lot_code,
        isCreate: lot.persisted_lot_code_id == null,
      },
      ...options,
    ];
  }

  const selectedPersistedIds = _computed(() =>
    visibleLots.value.map((lot) => lot.persisted_lot_code_id).filter((id) => id != null)
  );

  function disabledValuesFor(lot) {
    return selectedPersistedIds.value.filter((id) => id !== lot.persisted_lot_code_id);
  }

  const duplicatePersistedIds = _computed(() => {
    const seen = new Set();
    const duplicates = new Set();
    selectedPersistedIds.value.forEach((id) => (seen.has(id) ? duplicates.add(id) : seen.add(id)));
    return duplicates;
  });

  function lotIsDuplicate(lot) {
    return (
      lot.persisted_lot_code_id != null && duplicatePersistedIds.value.has(lot.persisted_lot_code_id)
    );
  }

  const hasDuplicateLots = _computed(() => duplicatePersistedIds.value.size > 0);

  function selectLot(lot, option) {
    lot.persisted_lot_code_id = option.persisted_lot_code_id ?? null;
    lot.lot_code = option.lot_code ?? "";
    lot.remaining_in_unit = option.remaining_in_unit ?? null;
    lot.remaining = option.remaining ?? null;
    emitChange();
  }

  function createLot(lot, lotCode) {
    lot.persisted_lot_code_id = null;
    lot.lot_code = lotCode;
    lot.remaining_in_unit = null;
    lot.remaining = null;
    emitChange();
  }

  function clearLot(lot) {
    lot.persisted_lot_code_id = null;
    lot.lot_code = "";
    lot.remaining_in_unit = null;
    lot.remaining = null;
    emitChange();
  }

  const visibleLots = _computed(() => lots.value.filter((lot) => !lot._destroy));
  const isMulti = _computed(() => visibleLots.value.length > 1);

  const hasPendingDeletion = _computed(() => lots.value.some((lot) => lot._destroy));
  const hasQuantityChanged = _computed(() => lots.value.some(lotQuantityChanged));

  function round(value) {
    return Number(value.toFixed(6));
  }

  const lotsTotal = _computed(() =>
    visibleLots.value.reduce((sum, lot) => sum + (Number(lot.quantity) || 0), 0)
  );
  const remaining = _computed(() =>
    targetQuantity.value == null ? null : round(targetQuantity.value - lotsTotal.value)
  );
  const hasMismatch = _computed(() => isMulti.value && remaining.value !== null && remaining.value !== 0);
  const isShort = _computed(() => hasMismatch.value && remaining.value > 0);
  const isOver = _computed(
    () => targetQuantity.value != null && targetQuantity.value > 0 && round(lotsTotal.value - targetQuantity.value) > 0
  );
  function topUpExceedsRemaining(lot) {
    const cap = lotRemaining(lot);
    return cap != null && round((Number(lot.quantity) || 0) + remaining.value - cap) > 0;
  }

  // Topping up a short total always grows the lot, so any lot will do. Trimming an
  // over total shrinks the lot, so it only works when the lot is bigger than the
  // overage -- otherwise it would drop to zero or below (remove the row instead).
  function canBalanceLot(lot) {
    if (isShort.value) return true;
    if (!isOver.value) return false;
    return round((Number(lot.quantity) || 0) + remaining.value) > 0;
  }

  function balanceHint(lot) {
    if (isShort.value) {
      const base = `Top up this lot by ${remaining.value} ${props.unit}`.trimEnd();
      if (topUpExceedsRemaining(lot)) {
        return `${base} -- more than what's left, so it records as a negative lot`;
      }
      return base;
    }
    if (isOver.value) {
      const overBy = `${Math.abs(remaining.value)} ${props.unit}`.trim();
      if (!canBalanceLot(lot)) {
        return `This lot is smaller than the ${overBy} over -- trim another lot or remove a row`;
      }
      return `Trim this lot by ${overBy}`;
    }
    return "Lots already add up to the amount used";
  }

  const mismatchLabel = _computed(() => {
    if (!hasMismatch.value) return "";
    const amount = `${Math.abs(remaining.value)} ${props.unit}`.trim();
    return isShort.value ? `${amount} short -- top up a lot code` : `${amount} over -- trim a lot code`;
  });

  function lotHasCode(lot) {
    return (lot.lot_code ?? "").trim() !== "";
  }

  function lotHasQuantity(lot) {
    return lot.quantity !== null && lot.quantity !== undefined && lot.quantity !== "";
  }

  function lotCodeMissing(lot) {
    return !lotHasCode(lot) && (isMulti.value || requiresLotCode.value);
  }

  function lotCodeErrors(lot) {
    if (lotIsDuplicate(lot)) {
      return JSON.stringify({ "Lot code": ["lot code is used by more than one row"] });
    }
    if (lotCodeMissing(lot)) {
      return JSON.stringify({ "Lot code": ["is required"] });
    }
    return null;
  }

  function quantityErrors(lot) {
    if (!isMulti.value) return null;
    if (!lotHasQuantity(lot)) {
      return JSON.stringify({ Quantity: ["is required"] });
    }
    if (!(Number(lot.quantity) > 0)) {
      return JSON.stringify({ Quantity: ["must be greater than 0"] });
    }
    return null;
  }

  function lotHasError(lot) {
    return Boolean(lotCodeErrors(lot) || quantityErrors(lot));
  }

  const hasFieldErrors = _computed(() => visibleLots.value.some(lotHasError));
  const isInvalid = _computed(() => isOver.value || hasDuplicateLots.value || hasFieldErrors.value);

  const serializedLots = _computed(() =>
    lots.value
      .filter((lot) => lot.id || lot.lot_code?.trim())
      .map((lot) => {
        const out = {
          lot_code: lot.lot_code ?? "",
          quantity: lot.quantity,
          persisted_lot_code_id: lot.persisted_lot_code_id ?? null,
        };
        if (lot.id) out.id = lot.id;
        // Single-lot mode only offers the combobox "clear" (no remove button), so a
        // cleared persisted lot must be destroyed on save rather than saved blank.
        const clearedSingleLot = !isMulti.value && lot.id && !lot.lot_code?.trim();
        if (lot._destroy || clearedSingleLot) out._destroy = true;
        return out;
      })
  );

  const initialSerializedLots = JSON.stringify(serializedLots.value);
  const hasChanges = _computed(() => JSON.stringify(serializedLots.value) !== initialSerializedLots);

  function emitChange(removeQuiet = true) {
    if (removeQuiet) quiet.value = false;
    emit("change", lots.value);
  }

  function updateQuantity(lot, value) {
    lot.quantity = normalizeQuantity(value);
    emitChange();
  }

  function addLotCode() {
    if (visibleLots.value.length === 1) {
      const firstLot = visibleLots.value[0];
      const total = targetQuantity.value ?? firstLot.quantity ?? 0;
      const firstRemaining = lotRemaining(firstLot);
      // When the first lot can't cover the total, default it to what's left and
      // drop the overflow on the new row -- the reason you usually split. Otherwise
      // there's no overflow to place, so fall back to an even split.
      const firstShare =
        firstRemaining != null && firstRemaining < total ? firstRemaining : round(total / 2);
      firstLot.quantity = firstShare;
      lots.value.push({
        lot_code: "",
        quantity: round(total - firstShare),
        persisted_lot_code_id: null,
        _originalLotCode: EMPTY_LOT_CODE,
        _originalQuantity: null,
      });
    } else {
      lots.value.push({
        lot_code: "",
        quantity: null,
        persisted_lot_code_id: null,
        _originalLotCode: EMPTY_LOT_CODE,
        _originalQuantity: null,
      });
    }
    emitChange();
    focusNewLotCode();
  }

  async function focusNewLotCode() {
    await nextTick();
    const fields = rootEl.value?.querySelectorAll("[data-lot-code-input]");
    fields?.[fields.length - 1]?.querySelector("input, button")?.focus();
  }

  function balanceLot(lot) {
    if (!canBalanceLot(lot)) return;
    lot.quantity = round((Number(lot.quantity) || 0) + remaining.value);
    emitChange();
  }

  function removeLotCode(lot) {
    if (lot.id) {
      lot._destroy = true;
    } else {
      lots.value.splice(lots.value.indexOf(lot), 1);
    }
    if (visibleLots.value.length === 1 && targetQuantity.value != null) {
      matchLoneLotToAmount(visibleLots.value[0]);
    }
    emitChange();
  }

  // The user can't update single lot quantities, so if we change the target
  // quantity, the lot should match it
  watch(
    () => targetQuantity.value,
    () => {
      if (visibleLots.value.length !== 1) return;
      matchLoneLotToAmount(visibleLots.value[0]);
      emitChange();
    }
  );
</script>

<template>
  <div
    ref="rootEl"
    class="production-run-lot-codes"
    :class="{ 'is-multi': isMulti }"
    data-lot-codes-editor
    :data-errors-quiet="quiet ? '' : null"
    :data-lot-codes-changed="hasChanges || null"
    :data-lot-codes-invalid="isInvalid || null"
  >
    <div class="lot-rows">
      <template v-for="(lot, index) in lots" :key="lot.id ?? index">
        <div
          v-if="!lot._destroy"
          class="lot-row"
          :class="{ 'has-lot-error': lotHasError(lot) }"
          data-lot-code-row
        >
          <div
            v-field-errors
            class="lot-input"
            :data-errors="lotCodeErrors(lot)"
            :data-lot-code="lot.lot_code"
            data-lot-code-input
          >
            <LotCodeCombobox
              :model-value="lotValue(lot)"
              :items="lotOptions(lot)"
              :source-url="openLotCodesUrl"
              :search-remotely="hasUnseededOpenLots"
              :seed="openLotCodes"
              :seed-total="openLotCodesCount"
              :unit="unit"
              :disabled="disabled"
              :disabled-values="disabledValuesFor(lot)"
              :has-error="!!lotCodeErrors(lot)"
              :is-new="lotIsNew(lot)"
              :new-lot-hint="NEW_LOT_HINT"
              :is-negative="lotIsNegative(lot)"
              :negative-hint="negativeLotHint(lot)"
              :is-duplicate="lotIsDuplicate(lot)"
              :duplicate-hint="DUPLICATE_LOT_HINT"
              :input-class="[
                'is-small',
                { 'changed-actual': lotCodeChanged(lot) && !lotCodeErrors(lot) },
              ]"
              placeholder="Lot code"
              @select="selectLot(lot, $event)"
              @create="createLot(lot, $event)"
              @clear="clearLot(lot)"
            />
          </div>

          <template v-if="isMulti">
            <input
              v-field-errors
              class="input is-small qty-input"
              :class="{ 'changed-actual': lotQuantityChanged(lot) && !quantityErrors(lot) }"
              type="number"
              step="any"
              min="0"
              placeholder="Qty"
              :value="lot.quantity"
              :disabled="disabled"
              :data-errors="quantityErrors(lot)"
              data-lot-code-quantity
              @input="updateQuantity(lot, $event.target.value)"
            />
            <span v-if="unit" class="unit has-text-grey mr-2">{{ unit }}</span>
            <WrapWithHint class="lot-action" :content="balanceHint(lot)" trigger="mouseenter focus">
              <button
                type="button"
                class="button is-small is-info is-outlined"
                :disabled="disabled || !canBalanceLot(lot)"
                data-top-up-lot
                @click="balanceLot(lot)"
              >
                <span class="icon is-small" :class="{ 'is-flipped': !isShort }">
                  <i class="fa-solid fa-arrow-up"></i>
                </span>
              </button>
            </WrapWithHint>
            <WrapWithHint class="lot-action" content="Stop using this lot code">
              <button
                type="button"
                class="button is-small is-danger is-outlined"
                :disabled="disabled"
                data-remove-lot-code
                @click="removeLotCode(lot)"
              >
                <span class="icon is-small"><i class="fa-solid fa-times"></i></span>
              </button>
            </WrapWithHint>
          </template>
        </div>
      </template>
    </div>

    <p
      v-if="hasMismatch"
      class="help is-danger lot-total-mismatch has-text-weight-semibold"
      data-lot-total-mismatch
    >
      <span class="icon is-small"><i class="fa-solid fa-exclamation-triangle"></i></span>
      <span>{{ mismatchLabel }}</span>
    </p>

    <button
      v-if="!disabled"
      type="button"
      class="button is-small is-info is-outlined"
      :title="isMulti ? null : 'Add lot code'"
      data-add-lot-code
      @click="addLotCode"
    >
      <span class="icon is-small"><i class="fa-solid fa-plus"></i></span>
      <span v-if="isMulti">Add lot code</span>
    </button>

    <input
      type="hidden"
      data-lot-codes-json
      :class="{ 'changed-actual': hasPendingDeletion || hasQuantityChanged }"
      :value="JSON.stringify(serializedLots)"
    />
  </div>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .production-run-lot-codes {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.375rem;
    width: 100%;

    .lot-rows {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      flex: 1 1 auto;
      min-width: 0;
    }

    .lot-row {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .lot-row.has-lot-error,
    .lot-row:has(.lot-code-required-missing) {
      border-left: 2px solid $danger;
      padding-left: 0.1rem;
    }

    .lot-input {
      flex: 1 1 auto;
      min-width: 11rem;

      :deep(.base-combobox) {
        width: 100%;
      }
    }

    .qty-input {
      width: 7ch;
    }

    .lot-action .icon.is-flipped {
      // We do it this way because switching the icon class with our
      // FontAwesome setup does not work
      transform: rotate(180deg);
    }

    .unit {
      font-size: 0.85em;
    }

    &.is-multi {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .lot-rows {
        width: 100%;
        gap: 0.5rem;
      }

      .lot-row {
        width: 100%;
        flex-wrap: wrap;
        row-gap: 0.25rem;
      }

      .lot-input {
        flex: 1 1 14rem;
        width: auto;
        min-width: 12rem;
      }

      .qty-input {
        flex: 0 1 11ch;
        width: auto;
        min-width: 8ch;
      }

      .unit {
        flex: 0 1 auto;
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .lot-action {
        flex: none;
      }

      .lot-total-mismatch {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        max-width: 24rem;
        margin-top: 0.25rem;
        margin-bottom: 0;

        .icon {
          flex: none;
        }
      }

      [data-add-lot-code] {
        align-self: flex-start;
        flex: none;
        width: auto;
        white-space: nowrap;
      }
    }
  }
</style>
